
import { useProfileQuery } from './useProfile/useProfileQuery';
import { useProfileMutations } from './useProfile/useProfileMutations';
import type { UserProfile } from "@/types/profile";
import { useRetry } from './useRetry';
import { useErrorTracking } from './useErrorTracking';
import { useConflictDetection } from './useConflictDetection';
import { useLogProfileActivity, useCreateProfileSnapshot } from './useProfileActivity';
import { useAuth } from '@/contexts/AuthContext';
import { showErrorNotification } from '@/utils/errorNotifications';

export function useProfileState() {
  const { data: profile, isLoading, error } = useProfileQuery();
  const { updateProfile: updateProfileMutation } = useProfileMutations();
  const { captureError } = useErrorTracking();
  const { resolveConflict, checkForConflicts } = useConflictDetection();
  const logActivity = useLogProfileActivity();
  const createSnapshot = useCreateProfileSnapshot();
  const { user } = useAuth();
  // Return current profile data or empty profile structure if loading/no data
  const currentProfile = profile || {
    legal_first_name: "",
    legal_last_name: "",
    business_email: "",
  } as UserProfile;

  const updateOperation = async (field: keyof UserProfile, value: any) => {
    return await updateProfileMutation({ [field]: value });
  };

  const { retry: retryUpdate } = useRetry(updateOperation, {
    maxAttempts: 3,
    retryableErrors: ['network', 'timeout', '5xx']
  });

  const updateProfile = async (field: keyof UserProfile, value: any) => {
    const oldValue = currentProfile[field];
    const profileUserId = user?.id;
    
    try {
      // Create snapshot before major changes
      if (profileUserId && ['legal_first_name', 'legal_last_name', 'business_email'].includes(field)) {
        await createSnapshot.mutateAsync({
          profileUserId,
          changeSummary: `Updated ${field.replace('_', ' ')}`
        });
      }

      await retryUpdate(field, value);

      // Log the activity after successful update
      if (profileUserId) {
        await logActivity.mutateAsync({
          profileUserId,
          actionType: 'update',
          fieldName: field,
          oldValue,
          newValue: value,
          isSensitive: ['personal_email', 'personal_phone', 'business_phone'].includes(field),
        });
      }
    } catch (error) {
      captureError(error as Error, {
        extra: { field, value, operation: 'updateProfile' }
      });
      
      if (error instanceof Error && error.message.includes('conflict')) {
        const hasConflict = checkForConflicts(profile as any, { [field]: value } as any);
        if (hasConflict) {
          resolveConflict('local');
        }
      } else {
        showErrorNotification({
          title: 'Failed to update profile',
          description: `Unable to update ${field}. Please try again.`,
          severity: 'medium'
        });
      }
      throw error;
    }
  };

  const updateContact = async (field: 'business_email' | 'personal_email' | 'business_phone' | 'personal_phone', value: string) => {
    const oldValue = currentProfile[field];
    const profileUserId = user?.id;
    
    await updateProfileMutation({
      [field]: value
    });

    // Log the activity after successful update
    if (profileUserId) {
      await logActivity.mutateAsync({
        profileUserId,
        actionType: 'update',
        fieldName: field,
        oldValue,
        newValue: value,
        isSensitive: ['personal_email', 'personal_phone', 'business_phone'].includes(field),
      });
    }
  };

  const updateAddress = async (type: 'home_address' | 'work_address', field: string, value: string) => {
    const currentAddress = currentProfile[type] as { [key: string]: string } | undefined;
    const oldValue = currentAddress?.[field];
    const profileUserId = user?.id;
    
    await updateProfileMutation({
      [type]: currentAddress ? {
        ...currentAddress,
        [field]: value
      } : { [field]: value }
    });

    // Log the activity after successful update
    if (profileUserId) {
      await logActivity.mutateAsync({
        profileUserId,
        actionType: 'update',
        fieldName: `${type}.${field}`,
        oldValue,
        newValue: value,
        isSensitive: false,
      });
    }
  };

  const resetProfile = () => {
    // TODO: Implement reset functionality with original data
  };

  return {
    profile: currentProfile,
    updateProfile,
    updateContact,
    updateAddress,
    resetProfile,
    isLoading,
    error,
  };
}
